import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import ReviewCard from './ReviewCard';

// Add this style to your CSS file or a <style> block to customize bullets perfectly
// .swiper-pagination-bullet-active { background-color: #D4AF37 !important; }

function ReviewContainer() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/active-reviews`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setReviews(data.reviews);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching reviews:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Helper function to calculate average rating
  const calculateAverageRating = (reviewsList) => {
    if (!reviewsList || reviewsList.length === 0) return 0;
    const sum = reviewsList.reduce((acc, review) => acc + (review.rating || 5), 0);
    return sum / reviewsList.length;
  };

  if (loading) {
    return (
      <div className="w-full h-96 flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  // UPDATED: Combined Error and Empty state with Friendly UI
  if (error || !reviews.length) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="w-full h-96 flex items-center justify-center bg-gray-50/50 rounded-2xl border border-[#D4AF37]/10">
            <div className="text-center">
                <p className="text-gray-400 text-lg font-medium tracking-wide" style={{ fontFamily: "Montserrat, serif" }}>
                    No reviews yet.
                </p>
                <p className="text-gray-300 text-sm mt-2">Be the first to share your experience!</p>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <Helmet>
        {/* JSON-LD Review Schema for each review */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "AggregateRating",
            "name": "Masho Perfumes - Customer Reviews",
            "ratingValue": calculateAverageRating(reviews).toFixed(1),
            "ratingCount": reviews.length,
            "bestRating": "5",
            "worstRating": "1"
          })}
        </script>
        {/* Individual Review Schemas */}
        {reviews.slice(0, 5).map((review, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Review",
              "author": {
                "@type": "Person",
                "name": review.authorName || "Customer"
              },
              "reviewRating": {
                "@type": "Rating",
                "ratingValue": (review.rating || 5).toString(),
                "bestRating": "5",
                "worstRating": "1"
              },
              "reviewBody": review.reviewText || review.comment || "",
              "datePublished": review.createdAt || new Date().toISOString().split('T')[0]
            })}
          </script>
        ))}
      </Helmet>
      <div className="relative">
        <Swiper
          modules={[Autoplay, Pagination]}
          spaceBetween={30}
          slidesPerView={1}
          autoplay={{
            delay: 4000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true
          }}
          loop={true}
          pagination={{
            clickable: true,
            el: '.review-pagination',
            type: 'bullets',
            // Updated to use your specific Gold color
            renderBullet: function (index, className) {
                return '<span class="' + className + '" style="background-color: #D4AF37; opacity: 0.4; transition: all 0.3s;"></span>';
            }
          }}
          breakpoints={{
            640: { slidesPerView: 1.5, spaceBetween: 20 },
            768: { slidesPerView: 2, spaceBetween: 25 },
            1024: { slidesPerView: 3, spaceBetween: 30 },
          }}
          className="pb-12"
        >
          {reviews.map((review) => (
            <SwiperSlide key={review.id} className="py-4 px-1">
              <ReviewCard review={review} />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Pagination Container */}
        {/* We use specific styling for the active bullet via global css or style tag, 
            but here we ensure the container is centered */}
        <div className="review-pagination flex justify-center mt-8 space-x-2 [&>.swiper-pagination-bullet-active]:!opacity-100 [&>.swiper-pagination-bullet-active]:!scale-125" />
      </div>
    </div>
  );
}

export default ReviewContainer;